import React, { useState, useEffect } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { formatDate, getTodayInAEST } from '../../utils/helpers';

// Show only last 4 digits of a microchip number
const chipLast4 = (chip) => {
  if (!chip) return null;
  const digits = chip.replace(/\D/g, '');
  return digits.length >= 4 ? `…${digits.slice(-4)}` : digits;
};

const COMMUNAL_CHAMBER = 'Communal / Equine';
const nowAEST = () => {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// ============================================================
// STEP 1 — START CYCLE MODAL (flame cremation)
// Pick a chamber, assign pet(s), record start time / gas / temp
// ============================================================
export const NewCycleModal = ({ onClose, onSuccess, preselectedChamber }) => {
  const [prepData, setPrepData] = useState(null);
  const [chambers, setChambers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [selectedPets, setSelectedPets] = useState([]);
  const [form, setForm] = useState({
    date: getTodayInAEST(),
    chamber: preselectedChamber || '',
    start_time: nowAEST(),
    gas_usage_mj: '',
    operating_temp_c: '',
    notes: ''
  });

  const isCommunal = form.chamber === COMMUNAL_CHAMBER;

  useEffect(() => {
    Promise.all([
      apiClient.get('/pet400/sessions/prepare-cycle'),
      apiClient.get('/pet400/chambers')
    ])
      .then(([prep, ch]) => {
        setPrepData(prep.data);
        setChambers(ch.data.chambers || []);
        // Preselect: single pet auto-ticks; communal pre-ticks all
        const pets = prep.data.pets || [];
        if (pets.length === 1) setSelectedPets([pets[0].id]);
      })
      .catch(() => showToast('Failed to load cycle data', 'error'))
      .finally(() => setLoading(false));
  }, []);

  // When switching to an individual chamber keep max 1 selected pet
  useEffect(() => {
    if (!isCommunal && selectedPets.length > 1) setSelectedPets(sel => sel.slice(0, 1));
    if (isCommunal && prepData?.pets?.length && selectedPets.length === 0) {
      setSelectedPets(prepData.pets.map(p => p.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.chamber]);

  const togglePet = (petId) => {
    setSelectedPets(sel => {
      if (sel.includes(petId)) return sel.filter(id => id !== petId);
      if (!isCommunal) return [petId]; // individual chamber: single selection
      return [...sel, petId];
    });
  };

  const selectedWeight = (prepData?.pets || [])
    .filter(p => selectedPets.includes(p.id))
    .reduce((sum, p) => sum + (p.weight_kg || 0), 0);

  const handleSubmit = async () => {
    if (!form.chamber)          { showToast('Please select a chamber', 'error'); return; }
    if (!selectedPets.length)   { showToast('Please select at least one pet', 'error'); return; }
    setSaving(true);
    try {
      await apiClient.post('/pet400/sessions', {
        date:             form.date,
        chamber:          form.chamber,
        linked_pet_ids:   selectedPets,
        tissue_weight_kg: Math.round(selectedWeight * 100) / 100,
        gas_usage_mj:     form.gas_usage_mj ? parseFloat(form.gas_usage_mj) : null,
        operating_temp_c: form.operating_temp_c ? parseFloat(form.operating_temp_c) : null,
        start_time:       form.start_time || null,
        notes:            form.notes
      });
      showToast(`✅ ${form.chamber} cycle started! Come back after the cremator finishes to complete it.`);
      onSuccess?.();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to start cycle', 'error');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto mb-3"></div>
        <p className="text-gray-600">Loading cycle data from CRM...</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[95vh] overflow-y-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 px-5 py-4 sm:rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <div>
            <h2 className="text-white font-bold text-lg">🔥 Step 1 — Start Cremation Cycle</h2>
            <p className="text-orange-100 text-xs">Cycle #{prepData?.next_cycle_number} · Fill BEFORE starting the cremator</p>
          </div>
          <button onClick={onClose} className="text-white text-2xl font-bold leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">

          {/* Step indicator */}
          <div className="flex items-center gap-2 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold">1</div>
              <span className="font-semibold text-orange-700">Load chamber & start</span>
            </div>
            <div className="h-px flex-1 bg-gray-200 mx-1"></div>
            <div className="flex items-center gap-1.5 opacity-40">
              <div className="w-6 h-6 rounded-full bg-gray-300 text-gray-500 flex items-center justify-center font-bold">2</div>
              <span className="text-gray-500">After cycle — record outcome</span>
            </div>
          </div>

          {/* Chamber selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">🏭 Chamber</label>
            <div className="grid grid-cols-3 gap-2" data-testid="chamber-selector">
              {chambers.map(c => {
                const busy = c.status === 'in_use';
                const selected = form.chamber === c.name;
                return (
                  <button key={c.name} disabled={busy}
                    onClick={() => setForm(f => ({ ...f, chamber: c.name }))}
                    className={`py-2 px-1 rounded-lg border-2 text-xs font-semibold transition-all ${
                      busy ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed' :
                      selected ? 'border-orange-500 bg-orange-50 text-orange-800 ring-2 ring-orange-300' :
                      'border-gray-200 bg-white text-gray-600 hover:border-orange-300'
                    } ${c.is_communal ? 'col-span-3' : ''}`}>
                    {c.is_communal ? '🐴 ' : '🔥 '}{c.name}
                    {busy && <span className="block text-[10px] font-normal">In use</span>}
                  </button>
                );
              })}
            </div>
            {form.chamber && (
              <p className="text-xs text-gray-500 mt-1.5">
                {isCommunal
                  ? '🐴 Communal / Equine chamber — multiple pets allowed.'
                  : `🔒 ${form.chamber} is an individual chamber — private cremation, exactly 1 pet.`}
              </p>
            )}
          </div>

          {/* Pet selection */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <h3 className="font-semibold text-orange-900 text-sm mb-2">
              🐾 Pets in Cremation Chambers queue {isCommunal ? '(select all that apply)' : '(select 1)'}
            </h3>
            {prepData?.pets?.length > 0 ? (
              <>
                <div className="space-y-1 mb-2">
                  {prepData.pets.map(p => {
                    const checked = selectedPets.includes(p.id);
                    return (
                      <label key={p.id} className={`flex items-center gap-2 text-xs rounded-lg px-2 py-1.5 cursor-pointer border ${
                        checked ? 'bg-white border-orange-400' : 'bg-orange-50 border-transparent hover:bg-white'
                      }`}>
                        <input type={isCommunal ? 'checkbox' : 'radio'} name="cycle-pet"
                          checked={checked} onChange={() => togglePet(p.id)}
                          className="w-4 h-4 text-orange-600 flex-shrink-0" />
                        <span className="text-orange-900 font-medium">{p.pet_name}</span>
                        <span className="text-orange-700">({p.client_name}) — {p.weight_kg}kg</span>
                        {chipLast4(p.microchip_number)
                          ? <span className="ml-auto font-mono bg-orange-100 text-orange-700 px-1 py-0.5 rounded">🔬 {chipLast4(p.microchip_number)}</span>
                          : <span className="ml-auto text-orange-300 italic">no chip</span>}
                      </label>
                    );
                  })}
                </div>
                <p className="text-sm font-semibold text-orange-900 pt-2 border-t border-orange-200">
                  Selected: <span className="text-orange-700">{selectedPets.length} pet(s) · {Math.round(selectedWeight * 100) / 100} kg</span>
                </p>
              </>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 font-medium">⚠️ No pets found in the Cremation Chambers location.</p>
                <p className="text-xs text-amber-700 mt-1">Move pets to “Cremation Chambers” in CRM first, then start the cycle.</p>
              </div>
            )}
          </div>

          {/* Date + Start time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
              <input type="date" value={form.date}
                onChange={e => setForm(f => ({...f, date: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Time</label>
              <input type="time" value={form.start_time}
                onChange={e => setForm(f => ({...f, start_time: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>

          {/* Gas + Temperature */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h3 className="font-semibold text-gray-800 text-sm">🔥 Cremator readings (optional)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Gas / Fuel Usage (MJ)</label>
                <input type="number" step="0.1" min="0" value={form.gas_usage_mj}
                  placeholder="e.g. 850"
                  onChange={e => setForm(f => ({...f, gas_usage_mj: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Operating Temp (°C)</label>
                <input type="number" step="1" min="0" max="1200" value={form.operating_temp_c}
                  placeholder="e.g. 850"
                  onChange={e => setForm(f => ({...f, operating_temp_c: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400" />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
              rows={2} placeholder="Any observations before starting..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 resize-none" />
          </div>

          {/* Reminder */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            ⏱️ After saving, start the cremator. Once the cycle finishes and the chamber has cooled, come back and tap <strong>“✅ Complete Cycle”</strong> to log hours, ashes outcome and move the pet(s) forward.
          </div>

          <button onClick={handleSubmit}
            disabled={saving || !prepData?.pets?.length}
            data-testid="start-cycle-button"
            className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl text-sm transition-colors">
            {saving ? '⏳ Starting...' : '🔥 Start Cremation Cycle'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// STEP 2 — COMPLETE CYCLE MODAL
// Filled AFTER the cremator finishes: hours, ashes, move pets
// ============================================================
export const CompleteCycleModal = ({ session, onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [sessionPets, setSessionPets] = useState([]);
  const [form, setForm] = useState({
    cycle_hours: '',
    end_time: nowAEST(),
    ashes_outcome: '',
    advance_pets: true,
    notes: ''
  });
  const [chipRecovery, setChipRecovery] = useState({}); // { pet_id: true/false }

  // Load pets + microchip data for this session
  useEffect(() => {
    if (!session?.id) return;
    apiClient.get(`/pet400/sessions/${session.id}/pets`)
      .then(res => {
        const pets = res.data.pets || [];
        setSessionPets(pets);
        const defaults = {};
        pets.forEach(p => { defaults[p.id] = true; });
        setChipRecovery(defaults);
      })
      .catch((e) => { console.error("Cycle load failed:", e); });
  }, [session?.id]);

  const handleSubmit = async () => {
    if (!form.cycle_hours)    { showToast('Please enter cycle hours', 'error');    return; }
    if (!form.ashes_outcome)  { showToast('Please select ashes outcome', 'error'); return; }
    setSaving(true);
    try {
      const microchip_recovery = sessionPets.map(p => ({
        pet_id:    p.id,
        pet_name:  p.pet_name,
        microchip: p.microchip_number || null,
        recovered: chipRecovery[p.id] ?? true
      }));

      const res = await apiClient.post(`/pet400/sessions/${session.id}/complete`, {
        cycle_hours:                parseFloat(form.cycle_hours),
        ashes_outcome:              form.ashes_outcome,
        advance_pets_to_dehydrator: form.advance_pets,
        end_time:                   form.end_time || undefined,
        notes:                      form.notes || undefined,
        microchip_recovery
      });
      const moved = res.data.moved_to_dehydrator || [];
      showToast(`✅ Cycle #${session.cycle_number} completed!${moved.length ? ` ${moved.length} pet(s) → Cooling / Ashes Processing.` : ''}`);
      onSuccess?.();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to complete cycle', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[95vh] overflow-y-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-teal-600 px-5 py-4 sm:rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <div>
            <h2 className="text-white font-bold text-lg">✅ Step 2 — Complete Cycle</h2>
            <p className="text-green-200 text-xs">Cycle #{session.cycle_number} · Fill AFTER the cremator finishes</p>
          </div>
          <button onClick={onClose} className="text-white text-2xl font-bold leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">

          {/* Step indicator */}
          <div className="flex items-center gap-2 text-xs">
            <div className="flex items-center gap-1.5 opacity-50">
              <div className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs">✓</div>
              <span className="text-gray-500">Chamber loaded & started</span>
            </div>
            <div className="h-px flex-1 bg-gray-200 mx-1"></div>
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center font-bold">2</div>
              <span className="font-semibold text-green-700">Record outcome</span>
            </div>
          </div>

          {/* Cycle summary */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="font-semibold text-green-900 text-sm">
              {session.chamber ? `🔥 ${session.chamber}` : 'Cycle'} — {formatDate(session.date)}
              {session.start_time ? ` · started ${session.start_time}` : ''}
            </p>
            <div className="flex flex-wrap gap-3 text-sm text-green-700 mt-1">
              <span>🐾 {session.total_pets} pet(s)</span>
              {session.dogs_count  > 0 && <span>🐕 {session.dogs_count}</span>}
              {session.cats_count  > 0 && <span>🐈 {session.cats_count}</span>}
              {session.other_count > 0 && <span>🐾 {session.other_count}</span>}
              <span>⚖️ {session.tissue_weight_kg}kg</span>
            </div>
            {(session.gas_usage_mj || session.operating_temp_c) && (
              <p className="text-xs text-green-600 mt-1">
                {session.gas_usage_mj ? `⛽ ${session.gas_usage_mj} MJ` : ''}
                {session.gas_usage_mj && session.operating_temp_c ? ' · ' : ''}
                {session.operating_temp_c ? `🌡️ ${session.operating_temp_c}°C` : ''}
              </p>
            )}
          </div>

          {/* Cycle hours + End time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">⏱️ Cycle Time (hours)</label>
              <input type="number" step="0.5" min="0.5" max="72"
                placeholder="e.g. 2.5" value={form.cycle_hours}
                onChange={e => setForm(f => ({...f, cycle_hours: e.target.value}))}
                className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-xl font-bold text-center focus:ring-2 focus:ring-green-400 focus:border-green-400" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">🕐 End Time</label>
              <input type="time" value={form.end_time}
                onChange={e => setForm(f => ({...f, end_time: e.target.value}))}
                className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-xl font-bold text-center focus:ring-2 focus:ring-green-400 focus:border-green-400" />
            </div>
          </div>

          {/* Ashes outcome */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">🏺 Ashes Colour</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { val: 'Light',   dot: 'bg-gray-100 border-gray-300',   label: '⬜ Light'   },
                { val: 'Greenish', dot: 'bg-green-300 border-green-500', label: '🟢 Greenish' },
                { val: 'Dark',    dot: 'bg-gray-900 border-gray-700',   label: '⬛ Dark'    },
              ].map(({ val, dot, label }) => (
                <button key={val}
                  onClick={() => setForm(f => ({...f, ashes_outcome: val}))}
                  className={`py-4 rounded-xl border-2 text-sm font-semibold transition-all ${
                    form.ashes_outcome === val
                      ? 'border-green-500 bg-green-50 ring-2 ring-green-400 ring-offset-1 scale-105'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-400'
                  }`}>
                  <div className={`w-8 h-8 rounded-full mx-auto mb-1.5 border-2 ${dot}`}></div>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Microchip Recovery Checklist */}
          {sessionPets.length > 0 && (
            <div className="border-2 border-purple-200 rounded-xl overflow-hidden">
              <div className="bg-purple-50 px-4 py-3 border-b border-purple-200">
                <p className="font-semibold text-sm text-purple-900">🔬 Microchip Recovery</p>
                <p className="text-xs text-purple-600 mt-0.5">Tick each chip that was physically recovered from the chamber</p>
              </div>
              <div className="divide-y divide-purple-100">
                {sessionPets.map(pet => {
                  const hasChip = !!pet.microchip_number;
                  const recovered = chipRecovery[pet.id] ?? true;
                  return (
                    <label key={pet.id}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                        !hasChip ? 'bg-gray-50 opacity-60' :
                        recovered ? 'bg-green-50' : 'bg-red-50'
                      }`}>
                      <input
                        type="checkbox"
                        checked={hasChip ? recovered : false}
                        disabled={!hasChip}
                        onChange={e => setChipRecovery(prev => ({ ...prev, [pet.id]: e.target.checked }))}
                        className="w-5 h-5 text-purple-600 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{pet.pet_name}</p>
                        {hasChip ? (
                          <p className="text-xs font-mono text-gray-500">🔬 {chipLast4(pet.microchip_number)}</p>
                        ) : (
                          <p className="text-xs text-gray-400 italic">No chip on file</p>
                        )}
                      </div>
                      {hasChip && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                          recovered ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {recovered ? '✅ Found' : '❌ Not found'}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              <div className="bg-purple-50 px-4 py-2 border-t border-purple-200 flex justify-between text-xs text-purple-700">
                <span>{Object.values(chipRecovery).filter(Boolean).length} recovered</span>
                <span>{sessionPets.filter(p => p.microchip_number).length} chips expected</span>
              </div>
            </div>
          )}

          {/* Move to Cooling / Ashes Processing */}
          <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
            form.advance_pets ? 'bg-teal-50 border-teal-400' : 'bg-gray-50 border-gray-200'
          }`}>
            <input type="checkbox" checked={form.advance_pets}
              onChange={e => setForm(f => ({...f, advance_pets: e.target.checked}))}
              className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm text-gray-900">Move pets → Cooling / Ashes Processing in CRM</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Updates location for all {session.total_pets} pet(s). Only tick when the cycle is fully complete and the chamber has cooled.
              </p>
            </div>
          </label>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
              rows={2} placeholder="Observations after cycle..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-400 resize-none" />
          </div>

          <button onClick={handleSubmit} disabled={saving}
            data-testid="complete-cycle-button"
            className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-semibold py-3 rounded-xl text-sm transition-colors">
            {saving ? '⏳ Completing...' : '✅ Complete Cycle & Record Outcome'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewCycleModal;
